import type { Game } from "../../../../royalur/Game.js";
import type { RollGameEvent } from "../../event/RollGameEvent.js";
import type { MoveGameEvent } from "../../event/MoveGameEvent.js";
import { GamePlayerData } from "../../../api/game/GamePlayerData.js";
import { PlayerType } from "../../../../royalur/model/PlayerType.js";
import { ListenerStore } from "../../../ListenerStore.js";
import { SimpleEngineProvider } from "../../../../royalur/engine/simple/SimpleEngineProvider.js";
import { Ruleset } from "../../Ruleset.js";
import type { LobbyStatus } from "../status/LobbyStatus.js";
import { Rune } from "../../../Rune.js";
import { GamePlayers } from "../../../api/game/GamePlayers.js";
import type { GameUpdateEvent } from "./GameUpdateEvent.js";
import type { AnalyticsProvider } from "../../../analytics/Analytics.js";
import type { LobbySettings } from "../../LobbySettings.js";
import type { MessageOutUploadGame } from "../../../api/game/MessageOutUploadGame.js";
import type { Engine } from "../../../../royalur/engine/Engine.js";
import type { GameAPI } from "../../../api/GameAPI.js";
import { UnknownLobbyPlayer } from "../../../lobby/UnknownLobbyPlayer.js";
import type { ClientError } from "../../error/ClientError.js";
import { Optional } from "../../../../util/Optional.js";
import type { APIEarnedAchievement, APIGameAnalysisOverview, APIGamePreferences } from "../../../api/api_schema.js";
import type { GameClientControls } from "../../../GameClientControls.js";
import type { ReactionType } from "../../ReactionType.js";
import { KnownLobbySettings } from "../../KnownLobbySettings.js";
import { TimeControlState } from "../../../lobby/TimeControlState.js";
import { RuneWithEvents } from "../../../RuneWithEvents.js";


export type StatusListener = (status: LobbyStatus) => void;


export type GameListener = (
    game: Game,
    skipAnimation: boolean,
) => void;


export type PlayerDataListener = (
    leftPlayer: PlayerType,
    yourPlayer: PlayerType | null,
    lightPlayer: GamePlayerData,
    darkPlayer: GamePlayerData,
) => void;


export type AnimateDiceRollingListener = () => void;


export type ReactionListener = (
    player: PlayerType | null,
    reaction: ReactionType,
) => void;


export type EarnedAchievementsListener = (
    achievements: APIEarnedAchievement[],
) => void;


export abstract class GameSource {
    private readonly analytics: AnalyticsProvider;
    private readonly gameAPI: GameAPI;
    private readonly setupSettings: LobbySettings;

    public readonly clientControls: Rune<GameClientControls>;
    public readonly preferences: Rune<APIGamePreferences>;
    public readonly lobbyStatus: Rune<LobbyStatus>;
    public readonly players: Rune<GamePlayers>;
    public readonly game: RuneWithEvents<Game, GameUpdateEvent>;
    public readonly analysisOverview: Rune<Optional<APIGameAnalysisOverview>>;
    public readonly timeControlState: Rune<TimeControlState>;
    public readonly error: Rune<Optional<ClientError>>;

    private readonly animateDiceRollingListeners: ListenerStore<AnimateDiceRollingListener>;
    private readonly reactionListeners: ListenerStore<ReactionListener>;
    private readonly earnedAchievementsListeners: ListenerStore<EarnedAchievementsListener>;

    protected constructor(
        initialClientControls: GameClientControls,
        initialPreferences: APIGamePreferences,
        analytics: AnalyticsProvider,
        gameAPI: GameAPI,
        setupSettings: LobbySettings,
        initialStatus: LobbyStatus,
        rules?: Engine,
        players?: GamePlayers,
        game?: Game,
    ) {
        this.analytics = analytics;
        this.gameAPI = gameAPI;
        this.setupSettings = setupSettings;

        this.clientControls = new Rune<GameClientControls>(initialClientControls);
        this.preferences = new Rune<APIGamePreferences>(initialPreferences);
        this.lobbyStatus = new Rune<LobbyStatus>(initialStatus);

        if (!players) {
            players = new GamePlayers(
                PlayerType.LIGHT, null,
                new UnknownLobbyPlayer(new GamePlayerData(1, PlayerType.LIGHT)),
                new UnknownLobbyPlayer(new GamePlayerData(2, PlayerType.DARK)),
            );
        }
        this.players = new Rune<GamePlayers>(players);

        if (!game) {
            if (rules) {
                game = rules.generateGame();
            } else {
                // Create a temporary game.
                const ruleProvider = new SimpleEngineProvider();
                const rules = ruleProvider.create(Ruleset.FINKEL.getSettings());
                game = rules.generateGame();
            }
        }
        this.game = new RuneWithEvents<Game, GameUpdateEvent>(game);
        this.analysisOverview = new Rune<Optional<APIGameAnalysisOverview>>(Optional.empty());
        this.timeControlState = new Rune<TimeControlState>(TimeControlState.EMPTY);
        this.error = new Rune<Optional<ClientError>>(Optional.empty());

        this.animateDiceRollingListeners = new ListenerStore<AnimateDiceRollingListener>();
        this.reactionListeners = new ListenerStore<ReactionListener>();
        this.earnedAchievementsListeners = new ListenerStore<EarnedAchievementsListener>();
    }

    /**
     * If more directives than this are added at once, directives
     * will start to be discarded.
     */
    abstract getControllerMaxDirectives(): number;

    getAnalytics(): AnalyticsProvider {
        return this.analytics;
    }

    getGameAPI(): GameAPI {
        return this.gameAPI;
    }

    getSetupSettings(): LobbySettings {
        return this.setupSettings;
    }

    getKnownSetupSettings(): KnownLobbySettings | null {
        return (this.setupSettings instanceof KnownLobbySettings ? this.setupSettings : null);
    }

    updatePreferences(preferences: APIGamePreferences): boolean {
        if (!this.preferences.set(preferences))
            return false;

        // Update preferences of your player as well.
        const players = this.players.get();
        const yourPlayer = players.getYourPlayer();
        if (yourPlayer) {
            const player = players.getPlayerByType(yourPlayer);
            const newPlayer = player.withPreferences(preferences);
            this.players.set(players.withPlayer(newPlayer));
        }
        return true;
    }

    recordAbortGame() {
        const game = this.game.get();
        const settings = this.getKnownSetupSettings();
        const lobbyStatus = this.lobbyStatus.get();
        if (settings && !game.isFinished() && lobbyStatus.isGameAvailable()) {
            this.analytics.recordAbortGame(settings);
        }
    }

    updateGameAndTimeControlState(
        game: Game,
        timeControlState: TimeControlState,
        gameEvent: GameUpdateEvent,
    ) {
        const emitTimeControlStateEvent = this.timeControlState.setAndDeferEvent(timeControlState);
        const emitGameEvent = this.game.setAndDeferEvent(game, gameEvent);
        emitTimeControlStateEvent();
        emitGameEvent();
    }

    updateGameAndPlayers(players: GamePlayers, game: Game, gameEvent: GameUpdateEvent) {
        const emitPlayerEvent = this.players.setAndDeferEvent(players, undefined);
        const emitGameEvent = this.game.setAndDeferEvent(game, gameEvent);
        emitPlayerEvent();
        emitGameEvent();
    }

    getRules(): Engine {
        return this.game.get().getRules();
    }

    addAnimateDiceRollingListener(listener: AnimateDiceRollingListener): () => void {
        return this.animateDiceRollingListeners.add(listener);
    }

    addReactionListener(listener: ReactionListener): () => void {
        return this.reactionListeners.add(listener);
    }

    addEarnedAchievementsListener(listener: EarnedAchievementsListener): () => void {
        return this.earnedAchievementsListeners.add(listener);
    }

    /**
     * Returns whether the given player type is a local human player
     * in the current game. This means that the interactions with the
     * game for this player will be made through the user interface,
     * and not received externally.
     */
    abstract isLocalHumanPlayer(playerType: PlayerType): boolean;

    handleRematch(): void {
        const clientControls = this.clientControls.get();
        clientControls.startGame(this.getSetupSettings());
    }

    cancelRematch(): void {
        // Nothing to do by default.
    }

    abstract handleResign(): void;

    abstract setup(): () => void;

    abstract handleRoll(event: RollGameEvent): void;

    abstract handleMove(event: MoveGameEvent): void;

    protected triggerDiceRollingAnimation() {
        this.animateDiceRollingListeners.invoke();
    }

    abstract handleReaction(player: PlayerType | null, reaction: ReactionType): void;

    protected triggerReaction(player: PlayerType | null, reaction: ReactionType) {
        this.reactionListeners.invoke(player, reaction);
    }

    protected triggerEarnedAchievements(achievements: APIEarnedAchievement[]) {
        this.earnedAchievementsListeners.invoke(achievements);
    }

    abstract shouldUploadGame(): boolean;

    abstract buildUploadGameMessage(): MessageOutUploadGame;
}
